import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Shield, Database, Users, Clock, Lock, Eye, Mail, Phone, Building } from 'lucide-react';
import { getSaveaPhone, getSaveaPhoneTel } from '../config/phoneConfig';

export const PrivacyPolicyPage = () => {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language === 'es';
  const country = isSpanish ? 'ES' : 'FR';
  const saveaPhone = getSaveaPhone(country, 'electricity', 'LP');

  if (isSpanish) {
    // VERSION ESPAGNOLE COMPLÈTE
    return (
      <div className="min-h-screen bg-gradient-to-br from-sage-50 to-white">
        <section className="py-16 lg:py-20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-sage-900 mb-4">
                Política de Privacidad
              </h1>
              <div className="text-sm text-sage-500">
                Última actualización: 01 Enero 2026
              </div>
            </div>

            {/* Objetivo y alcance */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Objetivo y alcance de la política</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sage-700 leading-relaxed">
                  SAVEA concede la mayor importancia y cuidado a la protección de la privacidad y los Datos Personales, así como al cumplimiento de lo dispuesto en la Legislación Aplicable.
                </p>
                <p className="text-sage-700 leading-relaxed mt-4">
                  El Reglamento (UE) 2016/679, de 27 de abril de 2016, relativo a la protección de las personas físicas en lo que respecta al tratamiento de datos personales y a la libre circulación de estos datos (en adelante, "RGPD") establece que los datos personales deben tratarse de forma lícita, leal y transparente. Por lo tanto, esta política de privacidad tiene como objetivo brindarle información simple y clara sobre el procesamiento de datos personales que le conciernen, en el contexto de su navegación y las operaciones realizadas en nuestro sitio web.
                </p>
              </CardContent>
            </Card>

            {/* Responsable del tratamiento */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Responsable del tratamiento</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sage-700 leading-relaxed">
                  Como parte de su actividad en el sitio SAVEA, recopilamos y utilizamos Datos Personales en relación con usted, la persona física ("Sujeto de Datos").
                </p>
                <p className="text-sage-700 leading-relaxed mt-4">
                  Para todas las Operaciones de Tratamiento, SOLUCIONES INTEGRALES HORIZON, S.L.U., sociedad registrada en España (NIF: B23972946, domicilio social: Calle Radas 57, Ático 1, 08004 Barcelona), representada por Julien Mercier (NIE: Y9783769C), determina los medios y fines del Tratamiento. Por lo tanto, actuamos como Controlador de datos, según las Regulaciones de Datos Personales, y en particular del Reglamento (UE) 2016/679.
                </p>
              </CardContent>
            </Card>

            {/* Datos recopilados */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>¿Qué datos personales recopilamos y cómo?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sage-700 leading-relaxed">
                  Al utilizar nuestro sitio web o contactar con nuestros servicios, nos proporciona cierta información sobre usted, parte de la cual es probable que lo identifique ("Datos personales"). Este es el caso cuando navega por nuestro sitio o cuando contacta con nuestros especialistas.
                </p>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-sage-800 mb-2">Datos de identificación:</h4>
                    <p className="text-sage-700 text-sm">
                      incluye cualquier información que nos permita identificarlo, como su nombre, apellidos, número de teléfono, cuando contacta voluntariamente con nuestros especialistas.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-sage-800 mb-2">Datos de navegación:</h4>
                    <p className="text-sage-700 text-sm">
                      También recopilamos su dirección IP con fines estadísticos y de mantenimiento. Al navegar por nuestro sitio web, interactúa con él. Como resultado, se recopila cierta información relacionada con su navegación mediante cookies técnicos.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact DPO */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Mail className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-blue-800">Contacto Protección de Datos</h3>
                </div>
                <p className="text-blue-700 text-sm mb-4">
                  Para ejercer sus derechos RGPD o cualquier consulta sobre protección de datos:
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-blue-600" />
                    <a href="mailto:info@savea.info" className="text-blue-700 hover:text-blue-900">info@savea.info</a>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-blue-600" />
                    <span className="text-blue-700">+34 613 072 408</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    );
  }

  // VERSION FRANÇAISE COMPLÈTE
  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-white">
      <section className="py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-sage-900 mb-4">
              Politique de Confidentialité
            </h1>
            <div className="text-sm text-sage-500">
              Dernière mise à jour : 01 Janvier 2026
            </div>
          </div>

          {/* Table des matières */}
          <Card className="mb-8 bg-sage-50">
            <CardHeader>
              <CardTitle>Table des matières</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-1 text-sm text-sage-700">
                <li>1. Objectif et champ d'application de la Politique</li>
                <li>2. Responsable de Traitement</li>
                <li>3. Quelles Données à caractère personnel collectons-nous et comment ?</li>
                <li>4. Pourquoi collectons-nous vos Données à caractère personnel et comment ?</li>
                <li>5. Partageons-nous vos Données à caractère personnel ?</li>
                <li>6. Combien de temps conservons-nous vos Données à caractère personnel ?</li>
                <li>7. Comment garantissons-nous la sécurité de vos Données à caractère personnel ?</li>
                <li>8. Quels sont vos droits ?</li>
                <li>9. Mise à jour de la présente Politique</li>
              </ol>
            </CardContent>
          </Card>

          {/* 1. Objectif et champ d'application */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-sage-600" />
                1. Objectif et champ d'application de la Politique
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sage-700 leading-relaxed">
                Nous accordons la plus grande importance et le plus grand soin à la protection de la vie privée et des Données à caractère personnel, ainsi qu'au respect des dispositions de la Législation applicable.
              </p>
              <p className="text-sage-700 leading-relaxed mt-4">
                Le Règlement (UE) 2016/679 du 27 avril 2016 relatif à la protection des personnes physiques à l'égard du Traitement des Données à caractère personnel et à la libre circulation de ces Données (ci-après « RGPD ») affirme que les Données à caractère personnel doivent être traitées de façon licite, loyale, et transparente. Ainsi, cette politique de confidentialité (ci-après la « Politique ») a pour objectif de vous fournir une information simple, claire sur les Traitements de Données à caractère personnel vous concernant, dans le cadre de votre navigation et des opérations réalisées sur notre site Internet.
              </p>
            </CardContent>
          </Card>

          {/* 2. Responsable de Traitement */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5 text-sage-600" />
                2. Responsable de Traitement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sage-700 leading-relaxed">
                Dans le cadre de votre activité sur le site SAVEA, nous collectons et utilisons, des Données à caractère personnel en lien avec vous, personnes physiques (ci-après « Personne concernée »).
              </p>
              <p className="text-sage-700 leading-relaxed mt-4">
                Pour l'ensemble des Traitements, SOLUCIONES INTEGRALES HORIZON, S.L.U., société enregistrée en Espagne (NIF : B23972946, siège social : Calle Radas 57, Ático 1, 08004 Barcelona), représentée par Julien Mercier (NIE : Y9783769C), détermine les moyens et les finalités du Traitement. Ainsi, nous agissons en tant que Responsable de Traitement, au sens de la Réglementation relative aux Données à caractère personnel, et notamment du Règlement (UE) 2016/679.
              </p>
            </CardContent>
          </Card>

          {/* Contact protection données */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <Mail className="w-5 h-5 text-blue-600" />
                <h3 className="font-semibold text-blue-800">Contact Protection des Données</h3>
              </div>
              <p className="text-blue-700 text-sm mb-4">
                Pour exercer vos droits RGPD ou toute question relative à la protection des données :
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-blue-600" />
                  <a href="mailto:info@savea.info" className="text-blue-700 hover:text-blue-900">info@savea.info</a>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-blue-600" />
                  <span className="text-blue-700">+34 613 072 408</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};